import React, { useState, useEffect, useCallback } from 'react';
import { supabaseTracked } from '../lib/api/supabaseWithTracking';

/**
 * ProductImagePicker
 * Shows all images from the Supabase "products" storage bucket.
 * User can click to select, or upload a new image.
 */
export default function ProductImagePicker({ currentUrl, onSelect, bucket = 'Products' }) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const BUCKET = bucket;
    // Use the exposed supabaseUrl from supabaseTracked for dynamic config
    const SUPABASE_STORAGE_URL = `${supabaseTracked.supabaseUrl}/storage/v1/object/public/${BUCKET}/`;

    const fetchImages = useCallback(async () => {
        setLoading(true);
        try {
            // DIAGNOSTIC LOG: Help identify the correct bucket name and visibility
            const { data: buckets } = await supabaseTracked.storage.listBuckets();
            if (buckets) console.log('[Storage] Available Buckets:', buckets.map(b => b.name));

            const { data, error } = await supabaseTracked.storage.from(BUCKET).list('', {
                limit: 200,
                sortBy: { column: 'name', order: 'asc' }
            });
            if (error) {
                console.error('[Storage] Error listing objects in bucket:', BUCKET, error);
                throw error;
            }
            console.log('[Storage] Raw data from bucket:', BUCKET, data);
            const imgs = (data || [])
                .filter(f => f.name && /\.(jpg|jpeg|png|gif|webp|avif)$/i.test(f.name))
                .map(f => ({ name: f.name, url: `${SUPABASE_STORAGE_URL}${encodeURIComponent(f.name)}` }));
            console.log('[Storage] Filtered images count:', imgs.length);
            setImages(imgs);
        } catch (err) {
            console.error('ProductImagePicker: error loading images', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) fetchImages();
    }, [open, fetchImages]);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const ext = file.name.split('.').pop();
            const safeName = `${Date.now()}-${Math.random().toString(36).substring(2, 7)}.${ext}`;
            const { error } = await supabaseTracked.storage.from(BUCKET).upload(safeName, file, {
                cacheControl: '3600',
                upsert: false
            });
            if (error) throw error;
            const url = `${SUPABASE_STORAGE_URL}${encodeURIComponent(safeName)}`;
            onSelect(url);
            await fetchImages();
            setOpen(false);
        } catch (err) {
            alert('Upload failed: ' + err.message);
        } finally {
            setUploading(false);
        }
    };

    const filteredImages = images.filter(img =>
        img.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="w-full">
            <div className="flex gap-2 items-center flex-wrap">
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className="px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 text-sm rounded-md hover:bg-indigo-100 font-medium"
                >
                    {open ? 'Close Picker' : 'Choose from Products Bucket'}
                </button>
                <label className="cursor-pointer px-3 py-1.5 bg-gray-100 border border-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-200 font-medium">
                    {uploading ? 'Uploading...' : '+ Upload New'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
                </label>
            </div>

            {open && (
                <div className="mt-2 border rounded-lg p-3 bg-gray-50 shadow-inner">
                    <input
                        type="text"
                        placeholder="Search images..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full mb-2 p-2 border rounded text-sm"
                    />
                    {loading ? (
                        <p className="text-sm text-gray-500">Loading images...</p>
                    ) : filteredImages.length === 0 ? (
                        <p className="text-sm text-gray-500">No images found. Upload one above.</p>
                    ) : (
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                            {filteredImages.map(img => (
                                <button
                                    key={img.name}
                                    type="button"
                                    onClick={() => { onSelect(img.url); setOpen(false); }}
                                    className={`relative rounded border-2 overflow-hidden hover:border-indigo-500 transition-colors ${
                                        currentUrl === img.url ? 'border-indigo-500' : 'border-gray-200'
                                    }`}
                                    title={img.name}
                                >
                                    <img
                                        src={img.url}
                                        alt={img.name}
                                        className="w-full h-14 object-cover"
                                        onError={e => { e.target.src = ''; }}
                                    />
                                    {currentUrl === img.url && (
                                        <div className="absolute inset-0 bg-indigo-500 bg-opacity-20 flex items-center justify-center">
                                            <span className="text-indigo-700 text-lg font-bold">&#10003;</span>
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
